import { useRef, useState } from 'react';
import { reversoSchema, type ReversoDto, type ReversoResponse } from '@triade/contracts';
import { Calculator } from 'lucide-react';
import { useReverso } from '@/api/precificacao';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ApiError } from '@/lib/api';
import { decimalDigitado, formatarCusto, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { lerFormacao } from './tipos';

type Referencia = Omit<ReversoDto, 'alvo' | 'valor' | 'base' | 'quantidade'>;

interface PainelReversoProps {
  /** Item, tabela, condicao, data (e, no simulador, os ajustes do cenario). Sem praticado: o reverso o define. */
  referencia: Referencia;
  /** Mostra "Usar como praticado" (praticado visivel e editavel na tela). */
  podeUsar: boolean;
  onUsarComoPraticado: (precoArredondado: string) => void;
  /** Texto do botao que usa o preco (padrao "Usar como praticado"; a Liberacao usa "Usar nesta condição"). */
  rotuloUsar?: string;
}

const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const TRIGGER = 'h-10 w-full rounded-[10px] bg-card';
const CAMPO = 'tabular w-full border-0 bg-transparent font-semibold outline-none';

function mensagem(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

/**
 * "Preço pelo resultado": o usuario diz quanto quer de lucro ou de MC (R$, por unidade ou no total) e a API devolve o preco.
 * A tela so monta o pedido, valida o formato pelo contrato (`reversoSchema`) e exibe o que a API calculou.
 */
export function PainelReverso({ referencia, podeUsar, onUsarComoPraticado, rotuloUsar = 'Usar como praticado', emDialog = false }: PainelReversoProps & { emDialog?: boolean }) {
  const reverso = useReverso();
  const [alvo, setAlvo] = useState<'LUCRO_RS' | 'MC_RS'>('LUCRO_RS');
  const [modo, setModo] = useState<'UNIDADE' | 'TOTAL'>('UNIDADE');
  const [valor, setValor] = useState('');
  const [quantidade, setQuantidade] = useState('');
  const [resultado, setResultado] = useState<ReversoResponse | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const candidato: ReversoDto = {
    ...referencia,
    alvo,
    base: modo,
    valor: decimalDigitado(valor) ?? '',
    ...(modo === 'TOTAL' ? { quantidade: decimalDigitado(quantidade) ?? '' } : {}),
  };
  const valida = reversoSchema.safeParse(candidato);
  const motivo = valida.success ? null : (valida.error.issues[0]?.message ?? 'Confira os valores');

  async function calcular() {
    if (!valida.success) return;
    setErro(null);
    try {
      setResultado(await reverso.mutateAsync(valida.data));
    } catch (e) {
      setResultado(null);
      setErro(mensagem(e));
    }
  }

  const lida = resultado ? lerFormacao(resultado.formacao) : null;
  const praticado = lida?.composicao.praticado ?? null;
  const mc = lida?.margemContribuicao?.praticado ?? null;

  return (
    <section
      aria-label={emDialog ? undefined : 'Preço pelo resultado'}
      className={emDialog ? 'flex flex-col gap-4' : 'flex flex-col gap-4 rounded-2xl border border-border bg-card p-5'}
    >
      {!emDialog && (
        <div>
          <h2 className="text-title font-semibold">Preço pelo resultado</h2>
          <p className="text-small text-ink-muted">Diga o lucro ou a margem de contribuição (R$) que quer, e veja o preço que entrega isso.</p>
        </div>
      )}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] items-end gap-3.5">
        <div>
          <Label htmlFor="rv-alvo" className={ROTULO}>Alvo</Label>
          <Select value={alvo} onValueChange={(v) => setAlvo(v as 'LUCRO_RS' | 'MC_RS')}>
            <SelectTrigger id="rv-alvo" className={TRIGGER}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="LUCRO_RS">Lucro em R$</SelectItem>
              <SelectItem value="MC_RS">MC em R$</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="rv-base" className={ROTULO}>Base</Label>
          <Select value={modo} onValueChange={(v) => setModo(v as 'UNIDADE' | 'TOTAL')}>
            <SelectTrigger id="rv-base" className={TRIGGER}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="UNIDADE">Por unidade</SelectItem>
              <SelectItem value="TOTAL">No total da quantidade</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {modo === 'TOTAL' && (
          <div>
            <Label htmlFor="rv-qtd" className={ROTULO}>Quantidade</Label>
            <div className="flex h-10 items-center rounded-[10px] border border-input bg-card px-3 focus-within:border-primary focus-within:shadow-focus">
              <input id="rv-qtd" type="text" inputMode="decimal" autoComplete="off" placeholder="0" value={quantidade} onChange={(e) => setQuantidade(e.target.value)} className={CAMPO} />
            </div>
          </div>
        )}
        <div>
          <Label htmlFor="rv-valor" className={ROTULO}>Valor em R$</Label>
          <div className="flex h-10 items-center gap-1.5 rounded-[10px] border border-input bg-card px-3 focus-within:border-primary focus-within:shadow-focus">
            <span aria-hidden="true" className="text-ink-muted">R$</span>
            <input id="rv-valor" type="text" inputMode="decimal" autoComplete="off" placeholder="0,00" value={valor} onChange={(e) => setValor(e.target.value)} className={CAMPO} />
          </div>
        </div>
        <button
          type="button"
          onClick={() => void calcular()}
          disabled={!valida.success || reverso.isPending}
          className="h-10 rounded-[10px] bg-primary px-3.5 font-medium text-primary-foreground hover:opacity-90 disabled:pointer-events-none disabled:opacity-50"
        >
          {reverso.isPending ? 'Calculando…' : 'Calcular preço'}
        </button>
      </div>
      {motivo && valor.trim() !== '' && <p className="text-caption text-ink-muted">{motivo}</p>}

      {erro && (
        <p role="alert" className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
          {erro}
        </p>
      )}

      {resultado && (
        <div className="flex flex-col gap-3 border-t border-dashed border-border pt-4" data-testid="resultado-reverso">
          <dl className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3">
            <div>
              <dt className="text-caption text-ink-muted">Preço exato</dt>
              <dd className="tabular font-semibold" data-testid="reverso-preco">{formatarCusto(resultado.preco)}</dd>
            </div>
            <div>
              <dt className="text-caption text-ink-muted">Preço arredondado</dt>
              <dd className="tabular text-metric font-semibold" data-testid="reverso-preco-arredondado">{formatarMoeda(resultado.precoArredondado)}</dd>
            </div>
            <div>
              <dt className="text-caption text-ink-muted">Alvo por unidade</dt>
              <dd className="tabular font-semibold">{formatarMoeda(resultado.valorPorUnidade)}</dd>
            </div>
            <div>
              <dt className="text-caption text-ink-muted">Lucro obtido (por unidade)</dt>
              <dd className="tabular font-semibold" data-testid="reverso-lucro">
                {praticado ? `${formatarMoeda(praticado.segmentos.lucro.valor)} · ${formatarFracaoPercentual(praticado.segmentos.lucro.fracao)}` : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-caption text-ink-muted">MC obtida (por unidade)</dt>
              <dd className="tabular font-semibold" data-testid="reverso-mc">
                {mc ? `${formatarMoeda(mc.mc)}${mc.mcPct ? ` · ${formatarFracaoPercentual(mc.mcPct)}` : ''}` : '—'}
              </dd>
            </div>
          </dl>
          {podeUsar && (
            <div>
              <button
                type="button"
                onClick={() => onUsarComoPraticado(resultado.precoArredondado)}
                className="h-10 rounded-[10px] border border-border bg-card px-3.5 font-medium hover:bg-line-soft"
              >
                {rotuloUsar}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

const MOTIVO_SEM_REFERENCIA = 'Escolha item, tabela e condição para usar o preço pelo resultado.';

interface PainelReversoDialogProps {
  /** null quando falta item, tabela ou condicao: o botao fica desabilitado com o motivo. */
  referencia: Referencia | null;
  /** Mostra "Usar como praticado" (praticado visivel e editavel na tela). */
  podeUsar: boolean;
  onUsarComoPraticado: (precoArredondado: string) => void;
  /** Campo que recebe o foco quando o pop-up fecha depois de usar o preco. */
  idCampoRetorno: string;
  /** Texto do botao que usa o preco (padrao "Usar como praticado"). */
  rotuloUsar?: string;
}

/**
 * Botao "Preço pelo resultado" ao lado do praticado. Abre o `PainelReverso` num pop-up.
 * Ao usar o preco, fecha o pop-up e devolve o foco ao campo do praticado. Esc fecha sem mudar nada.
 */
export function PainelReversoDialog({ referencia, podeUsar, onUsarComoPraticado, idCampoRetorno, rotuloUsar }: PainelReversoDialogProps) {
  const [aberto, setAberto] = useState(false);
  const usou = useRef(false);
  const desabilitado = referencia === null;

  return (
    <>
      <button
        type="button"
        aria-label="Preço pelo resultado"
        aria-disabled={desabilitado || undefined}
        title={desabilitado ? MOTIVO_SEM_REFERENCIA : 'Preço pelo resultado'}
        onClick={() => {
          if (!desabilitado) setAberto(true);
        }}
        className={`inline-flex h-10 shrink-0 items-center gap-1.5 rounded-[10px] border border-border bg-card px-3 text-small font-medium hover:bg-line-soft ${desabilitado ? 'cursor-not-allowed opacity-50' : ''}`}
      >
        <Calculator aria-hidden="true" className="size-4" />
        <span className="hidden sm:inline">Preço pelo resultado</span>
      </button>

      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent
          className="sm:max-w-2xl"
          onCloseAutoFocus={(e) => {
            if (!usou.current) return;
            usou.current = false;
            e.preventDefault();
            document.getElementById(idCampoRetorno)?.focus();
          }}
        >
          <DialogHeader>
            <DialogTitle>Preço pelo resultado</DialogTitle>
            <DialogDescription>Diga o lucro ou a margem de contribuição (R$) que quer, e veja o preço que entrega isso.</DialogDescription>
          </DialogHeader>
          {referencia && (
            <PainelReverso
              emDialog
              referencia={referencia}
              podeUsar={podeUsar}
              rotuloUsar={rotuloUsar}
              onUsarComoPraticado={(preco) => {
                usou.current = true;
                setAberto(false);
                onUsarComoPraticado(preco);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
